import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import axios from 'axios';
import Modal from './VehiculoModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo Vehículos";
const endpoint = ENDPOINTS.vehiculo;
const endpointById = ENDPOINTS.vehiculoById;
const modelo = Models.vehiculo;

// catálogo de tipos para enriquecer la tabla
const endpointTipo = ENDPOINTS.catTipoVehiculo;
const modeloTipo = Models.tipoVehiculo;

const maps = {
  mapCreate: (f) => ({
    idTipoVehiculo: Number(f.idTipoVehiculo) || 0,
    placa: f.placa,
    economico: f.economico,
    marca: f.marca,
    modelo: f.modelo,
    anio: Number(f.anio) || 0,
    color: f.color,
    capacidad: Number(f.capacidad) || 0,
  }),
  mapUpdate: (id, f) => ({
    idVehiculo: id,
    idTipoVehiculo: Number(f.idTipoVehiculo) || 0,
    placa: f.placa,
    economico: f.economico,
    marca: f.marca,
    modelo: f.modelo,
    anio: Number(f.anio) || 0,
    color: f.color,
    capacidad: Number(f.capacidad) || 0,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
 
  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Automóviles');
 
  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const isSuperAdmin = isSessionSuperAdmin();
      const sessionEmpresaId = getSessionEmpresaId();

      const [resVeh, resTipos] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointTipo),
      ]);

      let vehiculos = (resVeh.data || []).map((x) => modelo(x));
      const tipos = (resTipos.data || []).map((x) => modeloTipo(x));

      if (!isSuperAdmin) {
        vehiculos = vehiculos.filter(v => 
          v.idEmpresa == sessionEmpresaId || 
          v.idEmpresa === null || 
          v.idEmpresa === undefined
        );
      }

      // Enriquecer cada vehículo con el nombre del tipo
      vehiculos = vehiculos.map((v) => {
        const t = tipos.find((x) => x.idTipoVehiculo === v.idTipoVehiculo);
        return {
          ...v,
          tipoNombre: t ? (t.descripcion || t.clave) : `Tipo #${v.idTipoVehiculo}`,
        };
      });

      setRows(vehiculos);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const placa = (r.placa || '').toLowerCase();
      const marca = (r.marca || '').toLowerCase();
      const modeloV = (r.modelo || '').toLowerCase();
      const tipo = (r.tipoNombre || '').toLowerCase();
      const emp = (r.nombreEmpresa || '').toLowerCase();
      return (
        placa.includes(q) ||
        marca.includes(q) ||
        modeloV.includes(q) ||
        tipo.includes(q) ||
        emp.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idVehiculo === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idVehiculo ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por placa, marca, modelo, tipo o empresa…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Placa</th>
                  <th>Marca / Modelo</th>
                  <th>Tipo</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idVehiculo}>
                    <td><strong>{normalizeValue(r.placa)}</strong></td>
                    <td>{normalizeValue(`${r.marca} ${r.modelo}`)}</td>
                    <td>{normalizeValue(r.tipoNombre)}</td>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8' }}>
                        {r.nombreEmpresa || 'Global'}
                      </span>
                    </td>

                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idVehiculo)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}

                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            askDelete(r.idVehiculo, `${r.marca} ${r.modelo} (${r.placa})`);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={4 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {
          modalOpen && (
            <div className="roles-editor-slot" ref={editorRef}>
              <Modal
                open={modalOpen}
                mode={modalMode}
                data={editData}
                inline
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
              />
            </div>
          )
        }
      </div >

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
